import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import Stepper from 'components/base/stepper';
import { ROUTE_KEYS } from 'utils/constants';
import useEventDetailsContext from 'contexts/event-details.context';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage, formatPhoneNumber } from '@cokitchen/cokitchen-components';
import EventCreatedSuccessModal from 'pages/events/event-details/event-created-success-modal';
import { handleApiError } from 'utils/functions';
import { EventType } from 'models/event.model';

const EventDetailsContent = () => {
  const { setHideSidebar, setBackLinkTitle, setIsBackLink, setBackLink, activeCompany } =
    useDashboardContext();
  const { loading, setLoading, formData, setFormData, disabled } = useEventDetailsContext();

  const [, setIsLastPage] = useState(false); //isisLastPage and isFirstPage is yet to be used
  const { pathname } = useLocation();
  const [, setIsFirstPage] = useState([] as any);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [event, setEvent] = useState<EventType | null>(null);
  const companyId: string = activeCompany?.id;
  const navigate = useNavigate();
  const navUrl = [
    { name: 'Event details', url: '' },
    { name: 'Add your guests', url: 'add-your-guests' },
    { name: 'Top-up wallet', url: 'top-up-wallet' }
  ];

  useEffect(() => {
    setHideSidebar(true);
    setIsBackLink(true);
    setBackLinkTitle('Return to Events');
    setBackLink(ROUTE_KEYS.EVENTS);
    navigate(navUrl[0].url);
    return () => {
      setHideSidebar(false);
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  useEffect(() => {
    const path = pathname.slice(pathname.lastIndexOf('/') + 1, location.pathname.length);

    const totalPages = navUrl?.length - 1;
    const isFirstPage = navUrl.findIndex((element) => element.url === path);
    setIsFirstPage(isFirstPage);
    if (path === navUrl[totalPages]?.url) {
      setIsLastPage(true);
    }
  }, [pathname]);
  useEffect(() => {
    const sum = formData.guests.reduce((a, b) => a + Number(b.budget), 0);
    setFormData({ ...formData, budget: sum.toString() });
  }, [formData.guests]);

  useEffect(() => {
    const scrollToTop = () => {
      document.querySelector('.body_main')?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    };
    scrollToTop();
  }, []);

  const createEvent = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data: Record<string, string> = {
        name: formData.name,
        event_picture: formData.event_picture,
        start_date: formData.start_date,
        end_date: formData.end_date,
        company_id: companyId
      };
      if (formData.description) data.description = formData.description;
      const res = await apiService.post('events', { data });
      setEvent(res.data.data);
      toast(
        <ToastMessage
          type='success'
          message='Event created successfully. You can now add your guests'
        />
      );
    } catch (err) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const addGuests = async () => {
    if (!companyId) return;
    if (!event?.id) {
      toast(<ToastMessage type='error' message='Please create an event before adding guests' />);
      return false;
    }
    setLoading(true);
    try {
      const data = {
        company_id: companyId,
        event_id: event?.id,
        guest_data: formData.guests.map((item) => {
          return {
            first_name: item.first_name,
            last_name: item.last_name,
            email: item.email,
            phone_number: formatPhoneNumber(item.mobile_number ?? ''),
            budget: item.budget
          };
        })
      };
      await apiService.post('guest/csv', { data });
      toast(<ToastMessage type='success' message='Guests added successfully' />);
    } catch (err) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const topupWallet = async () => {
    if (!companyId) return;
    if (!event?.id) {
      toast(<ToastMessage type='error' message='Please create an event first' />);
      return;
    }
    try {
      setLoading(true);
      const data = {
        user_id: event?.user_id,
        amount: formData.budget,
        company_id: companyId,
        event_id: event?.id
      };
      await apiService.post('company/event/transfer', { data });
      toast(<ToastMessage type='success' message='Event topup successfully' />);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleNextClick = async (val: number) => {
    switch (val) {
      case 0:
        return await createEvent();
      default:
        break;
    }
  };

  const handleConfirmClick = async () => {
    await topupWallet();
    await addGuests();
    setShowSuccessModal(true);
  };

  return (
    <div className='flex justify-center event_form'>
      <Stepper
        onConfirm={handleConfirmClick}
        pages={navUrl}
        loading={loading}
        isDisabled={disabled}
        onClick={handleNextClick}>
        <Outlet />
      </Stepper>
      <EventCreatedSuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/events');
        }}
      />
    </div>
  );
};

export default EventDetailsContent;
